import type { Ref } from 'vue'

import {
  ARRIVAL_EXIT,
  ARRIVAL_REDUCED_EXIT,
  type ArrivalBeat,
  type ArrivalExit,
  type ArrivalSchedule,
  arrivalSchedule,
} from '@/lib/arrival/arrival-schedule'
import { blurIn, blurOut, FADE_OUT, pinInPlace, settled } from '@/lib/motion/motion-keyframes'
import type { OnboardingChainStepState } from '@/types/onboarding-chain'

import { type MotionSequence, useMotionSequence } from './useMotionSequence'

type ArrivalPart =
  | 'ground'
  | 'flight'
  | 'hero'
  | 'brand'
  | 'tile'
  | 'step'
  | 'rail'
  | 'content'
  | 'footer'

/** Plays the arrival: the bare screen leaving, then the shell arriving beneath it. */
export interface ArrivalSequence {
  /** True when the founder asked for reduced motion; the shell then only uncovers. */
  readonly reduced: Readonly<Ref<boolean>>
  /** Pins the bare screen where it stands and plays its exit. Resolves once the screen has left. */
  leave(screen: HTMLElement): Promise<void>
  /** Fades the bare ground away from the finished shell. Resolves once the shell is uncovered. */
  uncover(shell: Element): Promise<void>
  /** Uncovers the shell and plays its arrival. Resolves once every part has landed. */
  arrive(shell: Element, stepStates: readonly OnboardingChainStepState[]): Promise<void>
}

/** The spacing tokens the keyframes are measured in, read once per play. */
interface ArrivalTokens {
  /** The wordmark's blur and nudge. */
  readonly small: string
  /** The blur of the mark, the step rows and the footer, and the rise of every row and part. */
  readonly medium: string
  /** The blur of the page content and of the leaving screen. */
  readonly large: string
  /** How far beyond the right edge the tile waits, clearing its margin and its shadow. */
  readonly offstage: string
}

const HERO_START_SCALE = 0.82
const EXIT_SCALE = 0.985
const FLARE_PEAK = 0.35

const HIDDEN: Keyframe[] = [{ opacity: 0 }, { opacity: 0 }]
const LIGHT_UP: Keyframe[] = [{ '--lamp-on': 0 }, { '--lamp-on': 1 }]
const RAIL_DRAW: Keyframe[] = [{ transform: 'scaleY(0)' }, { transform: 'scaleY(1)' }]
const FLARE: Keyframe[] = [{ opacity: 0 }, { opacity: 1, offset: FLARE_PEAK }, { opacity: 0 }]

function selector(part: ArrivalPart): string {
  return `[data-arrival="${part}"]`
}

function one(root: Element | null, part: ArrivalPart): Element | null {
  return root?.querySelector(selector(part)) ?? null
}

function every(root: Element | null, part: ArrivalPart): Element[] {
  return root === null ? [] : Array.from(root.querySelectorAll(selector(part)))
}

function readTokens(motion: MotionSequence): ArrivalTokens {
  return {
    small: motion.token('--spacing-2'),
    medium: motion.token('--spacing-4'),
    large: motion.token('--spacing-6'),
    offstage: motion.token('--spacing-8'),
  }
}

/** The move that carries the flying mark from the centre of the shell onto the side-nav mark, measured on screen. */
function dockKeyframes(flight: Element, glyph: Element): Keyframe[] {
  const from = flight.getBoundingClientRect()
  const to = glyph.getBoundingClientRect()
  const x = to.left + to.width / 2 - (from.left + from.width / 2)
  const y = to.top + to.height / 2 - (from.top + from.height / 2)
  const scale = to.width / from.width
  return [
    { opacity: 1, transform: 'none' },
    { opacity: 1, transform: `translate(${x}px, ${y}px) scale(${scale})` },
  ]
}

function endOf(beat: ArrivalBeat): number {
  return beat.delay + beat.duration
}

/** Plays the arrival from `lib/arrival`'s schedule; every shell part animates toward the resting style its markup already shows. */
export function useArrivalSequence(): ArrivalSequence {
  const motion = useMotionSequence()

  function playEach(
    elements: readonly (Element | null)[],
    beats: readonly (ArrivalBeat | null | undefined)[],
    keyframes: Keyframe[],
  ): (Animation | null)[] {
    return elements.map((element, index) => {
      const beat = beats[index]
      return beat ? motion.play(element, keyframes, beat) : null
    })
  }

  function exit(): ArrivalExit {
    return motion.reduced.value ? ARRIVAL_REDUCED_EXIT : ARRIVAL_EXIT
  }

  async function leave(screen: HTMLElement): Promise<void> {
    pinInPlace(screen)
    const keyframes = motion.reduced.value
      ? FADE_OUT
      : blurOut(readTokens(motion).large, `scale(${EXIT_SCALE})`)
    await settled([motion.play(screen, keyframes, { ...exit().screen, fill: 'both' })])
  }

  function playGround(shell: Element): Animation | null {
    return motion.play(one(shell, 'ground'), FADE_OUT, exit().ground)
  }

  async function uncover(shell: Element): Promise<void> {
    await settled([playGround(shell)])
  }

  function playMark(
    shell: Element,
    schedule: ArrivalSchedule,
    tokens: ArrivalTokens,
  ): (Animation | null)[] {
    const brand = one(shell, 'brand')
    const glyph = brand?.querySelector('[data-mark="glyph"]') ?? null
    const word = brand?.querySelector('[data-mark="word"]') ?? null
    const flight = one(shell, 'flight')
    if (flight === null || glyph === null) return []

    return [
      motion.play(
        one(shell, 'hero'),
        blurIn(tokens.medium, `scale(${HERO_START_SCALE})`),
        schedule.markIn,
      ),
      motion.play(flight, dockKeyframes(flight, glyph), schedule.markDock),
      motion.play(glyph, HIDDEN, {
        delay: 0,
        duration: endOf(schedule.markDock),
        easing: 'standard',
      }),
      motion.play(
        word,
        blurIn(tokens.small, `translateX(calc(${tokens.small} * -1))`),
        schedule.wordmark,
      ),
    ]
  }

  function playTile(
    shell: Element,
    schedule: ArrivalSchedule,
    tokens: ArrivalTokens,
  ): (Animation | null)[] {
    const tile = one(shell, 'tile')
    return [
      motion.play(
        tile,
        [{ transform: `translateX(calc(100% + ${tokens.offstage}))` }, { transform: 'none' }],
        schedule.tile,
      ),
      ...playEach(
        every(tile, 'content'),
        schedule.content,
        blurIn(tokens.large, `translateY(${tokens.medium})`),
      ),
    ]
  }

  function playSideNav(
    shell: Element,
    schedule: ArrivalSchedule,
    tokens: ArrivalTokens,
  ): (Animation | null)[] {
    const steps = every(shell, 'step')
    const lamps = steps.map((step) => step.querySelector('[data-lamp]'))
    const flare = schedule.flare
    const flareLight =
      flare === null ? null : steps[flare.step]?.querySelector('[data-lamp-part="flare"]')

    return [
      ...playEach(steps, schedule.steps, blurIn(tokens.medium, `translateY(${tokens.medium})`)),
      ...playEach(every(shell, 'rail'), schedule.rails, RAIL_DRAW),
      ...playEach(lamps, schedule.lamps, LIGHT_UP),
      flare === null ? null : motion.play(flareLight, FLARE, { ...flare.beat, fill: 'none' }),
      motion.play(one(shell, 'footer'), blurIn(tokens.medium), schedule.footer),
    ]
  }

  async function arrive(
    shell: Element,
    stepStates: readonly OnboardingChainStepState[],
  ): Promise<void> {
    if (motion.reduced.value) return uncover(shell)
    const schedule = arrivalSchedule({
      stepStates,
      contentParts: every(one(shell, 'tile'), 'content').length,
    })
    const tokens = readTokens(motion)
    await settled([
      playGround(shell),
      ...playMark(shell, schedule, tokens),
      ...playTile(shell, schedule, tokens),
      ...playSideNav(shell, schedule, tokens),
    ])
  }

  return { reduced: motion.reduced, leave, uncover, arrive }
}
